import { useCallback } from "react"
import useTarifs from "../Hooks/useTarifs"
// import useModelsStore from "../stores/models"
import useCollections from "./useCollections"
// import usePrefStore from "../stores/prefs"
// import useTranslation from '../Hooks/useTranslation'
import glob_const from '../libs/glob_const'
import type { FilterCriteria, MinMaxPrice } from '../types'

/** Modèle traité avec les champs courts (collec, fam...) utilisés dans les filtres */
interface CatalogModel {
   id: string
   label: string
   desig: string
   argu: string
   gamme?: string
   collec?: string
   sous_collec?: string
   fam?: string
   porte?: string
   cycle?: string
   univers?: string
   reseau_ret?: boolean
   reseau_ere?: boolean
   reseau_eco?: boolean
   reseau_whs?: boolean
   type_bo?: string
   flg_best?: boolean
   flg_warn?: boolean
   flg_icon?: boolean
   flg_spo?: boolean
   flg_surco?: boolean
   [key: string]: unknown
}

const useFilters = () => {
   const { getColorsForModel } = useTarifs()
   const { getAllModelsAsArray, isExcluBoutique, isBOMono } = useCollections()

   /**
    * @param arr_source Tableau des modèles à filtrer
    * @param text Chaîne de caractère pour filtrage. Elle sera découpée en morceaux avec séparateur espace. Chaque morceau servant de critère de recherche (ReExp "contient")
    * @returns Tableau des modèles filtrés
    */
   const filterOnText = useCallback((arr_source: CatalogModel[], text: string | null | undefined) => {
      const str = text?.trim().toUpperCase()

      if (!str) {
         return arr_source
      } else {
         let arr_final: CatalogModel[] = []
         const parts_or = str.split(" ")  // Liste des blocs de texte à chercher (on applique un "ou" entre chaque bloc)
         for (const or_part of parts_or) {
            /* NON, ce test est effectué avant l'appel à filterOnText
               if (or_part.length >= minFilterLength) {
            */
            const parts_and = or_part.split("+")  // Liste des parties "et" dans un bloc de texte à chercher (on applique un "et" entre chaque partie)
            let arr_tempo = arr_source   // Au départ on considère que tout matche. On travaille sur les objets
            for (const and_part of parts_and) {
               /* NON, ce test est effectué avant l'appel à filterOnText
                  if (and_part.length >= minFilterLength) {
               */
               arr_tempo = arr_tempo.filter( // On remplace par un sous-ensemble => "ET" (i.e. intersection)
                  (item) => {
                     const item_id = item.id.toUpperCase()
                     const item_label = item.label.toUpperCase()
                     const item_desig = item.desig.toUpperCase()
                     const item_argu = item.argu.toUpperCase()
                     return (
                        ((and_part.length < 4) && item_id.startsWith(and_part)) ||  // De 1 à 3 car => "ID débute par"
                        ((and_part.length >= 4) && (
                           item_id.includes(and_part) ||   // 4 car et + => "ID contient"
                           item_desig.includes(and_part) ||
                           item_argu.includes(and_part)
                        )) ||
                        item_label.includes(and_part)
                     )
                  }
               )
               //}      // Fin if (and_part.length >= minFilterLength) {
            }
            // On ajoute ("OU", i.e. union) seulement les items inexistants (=>uniques)
            for (const item of arr_tempo) {
               if (arr_final.indexOf(item) === -1) {
                  arr_final.push(item);
               }
            }
            //}   // Fin if (or_part.length >= minFilterLength) {
         }
         return arr_final
      }
   }, [])

   const filterOnCriteriaExceptPrice = useCallback((arr_source: CatalogModel[], crits: FilterCriteria) => {
      let arr = arr_source

      // Si on a sélectionné des GAMMES
      if (crits.gammes?.length) {
         arr = arr.filter((model) => {
            return crits.gammes!.includes(model.gamme ?? '')
         })
      }

      // Si on a sélectionné des COLLECTIONS
      if (crits.collections?.length) {
         arr = arr.filter((model) => {
            return crits.collections!.includes(model.collec ?? '')
         })
      }

      // Si on a sélectionné des SOUS COLLECTIONS
      if (crits.sous_collections?.length) {
         arr = arr.filter((model) => {
            return crits.sous_collections!.includes(model.sous_collec ?? '')
         })
      }

      // Si on a sélectionné des FAMILLES
      if (crits.familles?.length) {
         arr = arr.filter((model) => {
            return crits.familles!.includes(model.fam ?? '')
         })
      }

      // Si on a sélectionné des PORTES
      if (crits.portes?.length) {
         arr = arr.filter((model) => {
            return crits.portes!.includes(model.porte ?? '')
         })
      }

      // Si on a sélectionné des COULEURS
      if (crits.couleurs?.length) {
         arr = arr.filter((item) => {
            const allColors = getColorsForModel(item.id)
            return crits.couleurs!.reduce((prev: boolean, cur: string) => {
               return prev || (allColors.colors?.includes(cur) ?? false)
            }, false)
         })
      }

      // Si on a sélectionné des CYCLES
      if (crits.cycles?.length) {
         arr = arr.filter((model) => {
            return crits.cycles!.includes(model.cycle ?? '')
         })
      }

      // Si on a sélectionné des UNIVERS
      if (crits.univers?.length) {
         arr = arr.filter((model) => {
            return crits.univers!.includes(model.univers ?? '')
         })
      }

      // Si on a sélectionné des PARAM COMM
      if (crits.param_comm?.length) {
         arr = arr.filter((model) => {
            // flg_best":false,"flg_warn":true,"flg_icon":false,"flg_spo":false,"flg_surco":false
            return (crits.param_comm!.includes("best") && model.flg_best) ||
               (crits.param_comm!.includes("warn") && model.flg_warn) ||
               (crits.param_comm!.includes("iconic") && model.flg_icon) ||
               (crits.param_comm!.includes("spo") && model.flg_spo) ||
               (crits.param_comm!.includes("surco") && model.flg_surco)
         })
      }

      // Si on a sélectionné BO Mono ou Paire
      if (crits.bo_mono_paire !== null && crits.bo_mono_paire !== 'all') {
         arr = arr.filter((model) => {
            return (model.fam !== glob_const.fam_bo || (crits.bo_mono_paire === 'mono' && isBOMono(model)) || (crits.bo_mono_paire === 'paire' && !isBOMono(model)))
         })
      }

      // Si on a sélectionné une EXCLU BOUTIQUE
      if (crits.exclusivite !== null && crits.exclusivite !== 'all') {
         arr = arr.filter((model) => {
            return ((crits.exclusivite === 'yes' && isExcluBoutique(model)) || (crits.exclusivite === 'no' && !isExcluBoutique(model)))
         })
      }

      return arr
   }, [getColorsForModel, isExcluBoutique, isBOMono])


   const filterOnPrice = useCallback((arr_source: CatalogModel[] | null | undefined, crits: FilterCriteria, all_minmax: Record<string, MinMaxPrice>) => {
      if (!arr_source || ((crits?.price_min === null) && (crits?.price_max === null))) {   // Si null ou undefined, on ne va pas plus loin...
         return arr_source
      }
      let arr = arr_source
      arr = arr.filter((item) => {
         const minMaxPrice = all_minmax[item.id]
         if (crits.price_min !== null) {
            if (crits.price_max !== null) {
               return ((minMaxPrice.min ?? 0) >= crits.price_min! && (minMaxPrice.max ?? 0) <= crits.price_max!)
            } else {
               return ((minMaxPrice.min ?? 0) >= crits.price_min!)
            }
         } else {
            return ((minMaxPrice.max ?? 0) < crits.price_max!)
         }
      })
      return arr
   }, [])

   const filterOnCriteria = useCallback((crits: FilterCriteria) => {
      return filterOnCriteriaExceptPrice(getAllModelsAsArray(), crits)
   }, [filterOnCriteriaExceptPrice, getAllModelsAsArray])

   return { filterOnText, filterOnCriteriaExceptPrice, filterOnPrice, filterOnCriteria }
}

export default useFilters
export type { CatalogModel }
